import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, PauseCircle, Warning } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const photo = (p) => (p ? `${BASE}/api/inspections/walk-photo/${p}` : null);

export default function FindingsApprovalPublic() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [dec, setDec] = useState({});
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    axios.get(`${BASE}/api/public/findings-approval/${token}`)
      .then(r => { setD(r.data); setDec(r.data.decisions || {}); setName(r.data.owner_name || ""); })
      .catch(e => setErr(e.response?.data?.detail || "Link expired or invalid"));
  }, [token]);
  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-slate-700 font-bold">{err}</div>;
  if (!d) return null;
  const decided = d.status && d.status !== "sent";
  const all = (v) => setDec(Object.fromEntries(d.findings.map(f => [f.key, v])));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/findings-approval/${token}/decide`, { decisions: dec, note, name });
      setResult(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Could not submit"); } finally { setBusy(false); }
  };
  const nApprove = Object.values(dec).filter(v => v === "approve").length;
  const nHold = Object.values(dec).filter(v => v === "hold").length;
  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="findings-approval-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white border-2 border-slate-900 shadow-[6px_6px_0_#0f172a] p-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{d.company} · Repair approval</div>
          <h1 className="text-2xl font-black uppercase tracking-tight mt-1">{d.property_address}{d.unit ? ` · #${d.unit}` : ""}</h1>
          <p className="text-sm text-slate-600 mt-1">Hi {d.owner_name} — we flagged {d.findings.length} item{d.findings.length === 1 ? "" : "s"} on our walk. Approve what you want dispatched; send anything back with a note.</p>
          {d.message && <p className="text-sm text-slate-800 mt-2 border-l-4 border-slate-300 pl-3 italic">{d.message}</p>}
        </div>

        {(result || decided) ? (
          <div className="bg-emerald-50 border-2 border-emerald-500 p-5" data-testid="findings-decided">
            <div className="font-black text-emerald-900 text-lg">Thanks — response recorded.</div>
            <div className="text-sm text-emerald-900 mt-1">
              {result ? `${result.approved} approved · ${result.held} sent back · ${result.wos_created} work orders created` : `Status: ${d.status.replace("_", " ")}${d.decided_at ? ` on ${new Date(d.decided_at).toLocaleDateString()}` : ""}`}
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <button onClick={() => all("approve")} className="h-9 px-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest" data-testid="approve-all">Approve all</button>
              <button onClick={() => all("hold")} className="h-9 px-3 border-2 border-slate-400 text-slate-700 text-[10px] font-black uppercase tracking-widest" data-testid="hold-all">Send all back</button>
            </div>
            <div className="space-y-3">
              {d.findings.map(f => (
                <div key={f.key} className={`bg-white border-2 p-3 flex gap-3 ${dec[f.key] === "approve" ? "border-emerald-500" : dec[f.key] === "hold" ? "border-slate-400 opacity-80" : "border-slate-200"}`} data-testid={`finding-${f.key}`}>
                  {photo(f.photo) ? <img src={photo(f.photo)} alt="" className="w-20 h-20 object-cover shrink-0 bg-slate-100"/> : <div className="w-20 h-20 bg-slate-100 shrink-0"/>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 text-white ${f.rating === "dmg" ? "bg-red-600" : "bg-amber-500"}`}>{f.rating === "dmg" ? "Damage" : "Fair"}</span>
                      <span className="text-[10px] text-slate-500 font-bold uppercase">{f.room}</span>
                    </div>
                    <div className="font-black text-slate-900 mt-0.5">{f.item}</div>
                    {f.note && <div className="text-xs text-slate-600">{f.note}</div>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setDec({ ...dec, [f.key]: "approve" })} className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${dec[f.key] === "approve" ? "bg-emerald-600 text-white" : "border border-emerald-600 text-emerald-700"}`} data-testid={`approve-${f.key}`}><CheckCircle weight="fill" size={12}/> Approve</button>
                      <button onClick={() => setDec({ ...dec, [f.key]: "hold" })} className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${dec[f.key] === "hold" ? "bg-slate-700 text-white" : "border border-slate-400 text-slate-600"}`} data-testid={`hold-${f.key}`}><PauseCircle weight="fill" size={12}/> Send back</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white border-2 border-slate-900 p-4 space-y-2">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full h-10 px-3 border border-slate-300 text-sm" data-testid="decide-name"/>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Notes for the crew (optional) — e.g. hold the blinds until tenant moves out" className="w-full px-3 py-2 border border-slate-300 text-sm" data-testid="decide-note"/>
              {nApprove + nHold < d.findings.length && <div className="text-xs text-amber-700 flex items-center gap-1"><Warning weight="fill"/> {d.findings.length - nApprove - nHold} item(s) not yet decided — they'll be treated as sent back.</div>}
              <button onClick={submit} disabled={busy || (nApprove + nHold === 0)} className="w-full h-12 bg-slate-900 text-white font-black uppercase tracking-widest text-sm disabled:opacity-40" data-testid="decide-submit">
                {busy ? "Sending…" : `Submit — approve ${nApprove}, send back ${d.findings.length - nApprove}`}
              </button>
            </div>
          </>
        )}
        <div className="text-[10px] text-slate-400 text-center">Powered by Field Radar</div>
      </div>
    </div>
  );
}
